import { FC, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import Image from "next/image";

import HtmlText from "@/sections/common/HtmlText";
import icon, { IIconName } from "@/utils/icon.helper";

// -------------------------------------------------------------------------------

export type IImageTextCardProps = HTMLAttributes<HTMLDivElement> & {
  icon?: IIconName;
  heading?: string;
  description?: ReactNode;
  variant?: "primary" | "secondary" | "white" | "light_primary";
  iconHeight?: number;
  iconWidth?: number;
  iconClass?: string;
};

export const bgColor = {
  primary: "bg-primary-100 dark:bg-black-800",
  secondary: "bg-secondary-50 dark:bg-black-800",
  white: "bg-white dark:bg-black-800",
  light_primary: "bg-primary-50 dark:bg-black-800",
};

const ImageTextCard: FC<IImageTextCardProps> = ({
  iconClass,
  icon: iconName,
  heading,
  description,
  variant = "white",
  className,
  iconHeight = 50,
  iconWidth = 50,
  ...other
}) => {
  return (
    <div
      className={clsx(
        "h-full w-full dark:hover:bg-gradient-to-l dark:hover:from-primary-900 dark:hover:to-black-800 dark:hover:transition-all dark:hover:duration-700 dark:hover:ease-in",
        variant === "white"
          ? "bg-white before:bg-primary-50"
          : ` ${bgColor[variant]} before:bg-white`,
        bgColor[variant],
        className,
      )}
      {...other}
    >
      <div>
        <div className="h-full w-full">
          {iconName && (
            <Image
              src={icon(iconName) ?? ""}
              alt=""
              className={clsx(
                "mb-2 size-8 md:mb-4 lg:size-10 xl:mb-7 xl:size-[50px]",
                iconClass ?? "",
              )}
              height={iconHeight}
              width={iconWidth}
            />
          )}
        </div>

        {heading && (
          <h3 className="mb-2 font-bricolage-grotesque text-h6 font-medium text-black-900 dark:text-white lg:mb-4 lg:text-h5 xl:mb-7 xl:text-h4">
            {heading}
          </h3>
        )}
      </div>
      {description && (
        <HtmlText className="font-normal text-gray-500 dark:text-black-200">
          {description}
        </HtmlText>
      )}
    </div>
  );
};

export default ImageTextCard;
